import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { patientAPI } from '../utils/api';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/Toaster';
import { ChevronRight, ChevronLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Card } from '../components/ui/card';
import api from '../utils/api';
import { useTranslation } from 'react-i18next';

const PatientOnboardingV2 = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [prefillLoaded, setPrefillLoaded] = useState(false);
  // P0-FIX: API-driven treatment categories
  const [apiCategories, setApiCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    gender: '',
    treatment_categories: [],
    budget_range: '',
    pricing_profile: [],
    trip_type: '',
    package_types: [],
    date_preference: '',
    country_of_origin: '',
  });

  // Prefill from saved onboarding data
  useEffect(() => {
    if (!user?.id || prefillLoaded) return;
    api.get(`/patient/onboarding/${user.id}`, {
    })
      .then(({ data }) => data).catch(() => (null))
      .then(data => {
        if (data) {
          // Normalize gender values from older data format
          const genderMap = { 'Man': 'Male', 'Woman': 'Female', 'man': 'Male', 'woman': 'Female' };
          const normalizedGender = genderMap[data.gender] || data.gender;
          
          // Normalize treatment categories from older format
          const validCategories = ["Women's Health", "Men's Health", 'Dental', 'Hair Transplant', 
            'Aesthetic Surgery', 'Bariatric / Weight Loss', 'Orthopedics', 'Dermatology / Skin', 'Fertility',
            'Mommy Makeover'];
          const normalizeTreatment = (t) => {
            if (validCategories.includes(t)) return t;
            // Map extended names to base categories  
            if (t.startsWith('Dental')) return 'Dental';
            if (t.startsWith('Hair')) return 'Hair Transplant';
            if (t.startsWith('Aesthetic') || t.includes('Makeover')) return 'Aesthetic Surgery';
            if (t.includes('Weight') || t.includes('Bariatric')) return 'Bariatric / Weight Loss';
            if (t.includes('Orthop')) return 'Orthopedics';
            if (t.includes('Derm') || t.includes('Skin')) return 'Dermatology / Skin';
            return t;
          };
          const normalizedCategories = Array.isArray(data.treatment_categories) 
            ? [...new Set(data.treatment_categories.map(normalizeTreatment))]
            : [];
          
          setFormData(prev => ({
            gender: normalizedGender || prev.gender,
            treatment_categories: normalizedCategories.length > 0 ? normalizedCategories : prev.treatment_categories,
            budget_range: data.budget_range || prev.budget_range,
            pricing_profile: Array.isArray(data.pricing_profile) ? data.pricing_profile : prev.pricing_profile,
            trip_type: data.trip_type || prev.trip_type,
            package_types: Array.isArray(data.package_types) ? data.package_types : 
              (data.trip_type ? data.trip_type.split(', ').filter(Boolean) : prev.package_types),
            date_preference: data.date_preference || prev.date_preference,
            country_of_origin: data.country_of_origin || prev.country_of_origin,
          }));
        }
        setPrefillLoaded(true);
      })
      .catch(() => setPrefillLoaded(true));
  }, [user?.id, prefillLoaded]);

  // P0-FIX: Fetch treatment categories from API
  const API = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/public/treatment-taxonomy`);
        const data = res.data;
        const cats = (data.categories || [])
          .filter(c => c.status !== 'disabled')
          .map(c => {
            // P0-3: Safely extract string — c.name may be multilingual object
            let label = c.label;
            if (!label || typeof label !== 'string') {
              const name = c.name;
              if (typeof name === 'string') label = name;
              else if (name && typeof name === 'object') label = name.en || Object.values(name).find(v => typeof v === 'string' && v.trim()) || '';
            }
            if (!label) label = c.slug || '';
            return {
              value: label,
              slug: c.slug,
              gender: 'all',
            };
          })
          .filter(c => c.value);
        if (cats.length > 0) {
          setApiCategories(cats);
        }
      } catch (err) {
        console.error('[Onboarding] Failed to fetch treatment categories:', err);
      }
    })();
  }, [API]);

  const totalSteps = 6;

  // Step 1: Gender options
  const genderOptions = [
    { value: 'Female', label: 'Female', icon: '👩' },
    { value: 'Male', label: 'Male', icon: '👨' },
    { value: 'Non-binary', label: 'Non-binary', icon: '🧑' },
    { value: 'Prefer not to say', label: 'Prefer not to say', icon: '❓' },
  ];

  // Step 2: Treatment categories (gender-aware) — P0-FIX: Uses API categories when available
  const getAllTreatments = () => {
    // If API categories loaded, use them
    if (apiCategories.length > 0) {
      return apiCategories.map(c => c.value);
    }
    // Fallback static list while API loads
    const baseCategories = [
      { value: "Women's Health", gender: ['Female', 'Non-binary'] },
      { value: "Men's Health", gender: ['Male'] },
      { value: 'Dental', gender: 'all' },
      { value: 'Hair Transplant', gender: 'all' },
      { value: 'Aesthetic Surgery', gender: 'all' },
      { value: 'Bariatric / Weight Loss', gender: 'all' },
      { value: 'Orthopedics', gender: 'all' },
      { value: 'Dermatology / Skin', gender: 'all' },
      { value: 'Fertility', gender: ['Female', 'Non-binary'] },
    ];

    if (!formData.gender || formData.gender === 'Prefer not to say') {
      return baseCategories.map(c => c.value);
    }

    return baseCategories
      .filter(c => c.gender === 'all' || c.gender.includes(formData.gender))
      .map(c => c.value);
  };

  // Step 3: Pricing profile
  const pricingProfiles = [
    { value: 'Standard', label: 'Standard', desc: 'Looking for quality at fair prices', icon: '💼' },
    { value: 'Budget-sensitive', label: 'Budget-sensitive', desc: 'Cost is a primary concern', icon: '💰' },
    { value: 'VIP / Premium', label: 'VIP / Premium', desc: 'Want the best, premium service', icon: '⭐' },
  ];

  // Step 3b: Budget range
  const budgets = [
    { value: 'Less than $2,000', label: 'Under $2,000' },
    { value: '$2,000 - $5,000', label: '$2,000 - $5,000' },
    { value: '$5,000 - $10,000', label: '$5,000 - $10,000' },
    { value: 'More than $10,000', label: 'Over $10,000' },
  ];

  // Step 4: Package types (multi-select)
  const packageTypes = [
    { value: 'Treatment', label: 'Treatment', desc: 'Medical treatment only', icon: '🏥' },
    { value: 'Treatment + Tourism', label: 'Treatment + Tourism', desc: 'Combine treatment with vacation', icon: '🏖️' },
    { value: 'VIP Package', label: 'VIP Package', desc: 'Premium all-inclusive experience', icon: '⭐' },
  ];

  // Step 5: Date preference
  const datePreferences = [
    { value: 'Flexible dates', label: 'Flexible dates' },
    { value: 'Specific month', label: 'Specific month' },
    { value: 'Specific date range', label: 'Specific date range' },
    { value: 'Weekend preferred', label: 'Weekend preferred' },
    { value: 'Weekdays preferred', label: 'Weekdays preferred' },
  ];

  // Step 6: Countries (full ISO list from API)
  const { t } = useTranslation();
  const [residenceCountry, setResidenceCountry] = useState('');
  const [countries, setCountriesData] = useState([]);
  useEffect(() => {
    api.get(`/public/catalog/countries`)
      .then(({ data }) => data)
      .then(d => { if (d.success) setCountriesData(d.countries); })
      .catch(() => {});
  }, []);
  // Prefill canonical residence (persons.residence_country) — never derived from country_of_origin.
  useEffect(() => {
    if (!user) return;
    api.get('/surface/residence')
      .then(({ data }) => { if (data?.declared && data.residence_country) setResidenceCountry(data.residence_country); })
      .catch(() => {});
  }, [user]);

  const toggleTreatment = (treatment) => {
    setFormData(prev => ({
      ...prev,
      treatment_categories: prev.treatment_categories.includes(treatment)
        ? prev.treatment_categories.filter(t => t !== treatment)
        : [...prev.treatment_categories, treatment]
    }));
  };

  const togglePricingProfile = (profile) => {
    setFormData(prev => ({
      ...prev,
      pricing_profile: prev.pricing_profile.includes(profile)
        ? prev.pricing_profile.filter(p => p !== profile)
        : [...prev.pricing_profile, profile]
    }));
  };

  const togglePackageType = (type) => {
    setFormData(prev => ({
      ...prev,
      package_types: prev.package_types.includes(type)
        ? prev.package_types.filter(t => t !== type)
        : [...prev.package_types, type]
    }));
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.gender !== '';
      case 2:
        return formData.treatment_categories.length > 0;
      case 3:
        return formData.pricing_profile.length > 0 && formData.budget_range !== '';
      case 4:
        return formData.package_types.length > 0;
      case 5:
        return formData.date_preference !== '';
      case 6:
        return formData.country_of_origin !== '' && residenceCountry !== '';
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (canProceed()) {
      setStep(step + 1);
    } else {
      toast({ title: 'Error', description: 'Please complete this step before continuing', variant: 'destructive' });
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    if (!canProceed()) {
      toast({ title: 'Error', description: 'Please complete all required fields', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      // Use existing user ID or create temp ID for testing
      const userId = user?.id || `temp_${Date.now()}`;
      
      // FUNNEL-INTEGRITY — residence is a distinct canonical fact (persons.residence_country),
      // never inferred from country_of_origin.
      if (user) {
        try {
          await api.post('/surface/residence', { residence_country: residenceCountry });
        } catch (resErr) {
          toast({ title: 'Error', description: t('residencePrompt.error'), variant: 'destructive' });
          setLoading(false);
          return;
        }
      }
      await patientAPI.createOnboarding({
        user_id: userId,
        ...formData,
        trip_type: formData.package_types.join(', '),
        treatment_interest: formData.treatment_categories.join(', '), // Backward compatibility
      });
      toast({ title: 'Success', description: 'Profile created successfully!' });
      
      // Only navigate if user is logged in
      if (user) {
        // Map onboarding selections to taxonomy slugs for Find Clinics
        const ONBOARDING_TO_TAXONOMY = {
          'Dental': 'dental',
          'Hair Transplant': 'aesthetics',
          'Aesthetic Surgery': 'aesthetics',
          'Bariatric / Weight Loss': 'bariatric',
          'Orthopedics': 'orthopedic',
          'Dermatology / Skin': 'dermatology',
          'Fertility': 'fertility',
          "Women's Health": 'wellness',
          "Men's Health": 'wellness',
        };
        const selectedSlugs = [...new Set(
          (formData.treatment_categories || [])
            .map(label => ONBOARDING_TO_TAXONOMY[label])
            .filter(Boolean)
        )];
        navigate('/patient/dashboard', {
          state: { treatmentSlugs: selectedSlugs },
        });
      } else {
        // Show success message for debug mode
        toast({ title: 'Success', description: 'Data saved! (Debug mode)' });
      }
    } catch (error) {
      console.error('Save error:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save profile', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900">What's your gender?</h2>
              <p className="text-sm text-gray-600 mt-1">This helps us match you with appropriate treatments</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {genderOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setFormData({...formData, gender: option.value})}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.gender === option.value
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  data-testid={`gender-${option.value}`}
                >
                  <div className="text-2xl mb-1">{option.icon}</div>
                  <div className="font-semibold text-sm">{option.label}</div>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900">What treatments interest you?</h2>
              <p className="text-sm text-gray-600 mt-1">Select all that apply</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {getAllTreatments().map((treatment) => (
                <button
                  key={treatment}
                  onClick={() => toggleTreatment(treatment)}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    formData.treatment_categories.includes(treatment)
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  data-testid={`treatment-${treatment.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                      formData.treatment_categories.includes(treatment)
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-300'
                    }`}>
                      {formData.treatment_categories.includes(treatment) && (
                        <CheckCircle className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <span className="font-medium text-sm">{treatment}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900">Pricing Preferences</h2>
              <p className="text-sm text-gray-600 mt-1">Help us understand your budget expectations</p>
            </div>
            
            {/* Pricing Profile */}
            <div>
              <h3 className="font-semibold text-sm mb-2">Pricing Approach (select all that apply)</h3>
              <div className="grid grid-cols-1 gap-2">
                {pricingProfiles.map((profile) => (
                  <button
                    key={profile.value}
                    onClick={() => togglePricingProfile(profile.value)}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      formData.pricing_profile.includes(profile.value)
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    data-testid={`pricing-${profile.value.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="text-xl">{profile.icon}</div>
                      <div className="flex-1">
                        <div className="font-semibold text-sm">{profile.label}</div>
                        <div className="text-xs text-gray-600">{profile.desc}</div>
                      </div>
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        formData.pricing_profile.includes(profile.value)
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300'
                      }`}>
                        {formData.pricing_profile.includes(profile.value) && (
                          <CheckCircle className="h-3 w-3 text-white" />
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Budget Range */}
            <div>
              <h3 className="font-semibold text-sm mb-2">Budget Range</h3>
              <div className="grid grid-cols-2 gap-2">
                {budgets.map((budget) => (
                  <button
                    key={budget.value}
                    onClick={() => setFormData({...formData, budget_range: budget.value})}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.budget_range === budget.value
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    data-testid={`budget-${budget.value}`}
                  >
                    <div className="font-medium text-sm">{budget.label}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900">Package Selection</h2>
              <p className="text-sm text-gray-600 mt-1">Select all that apply — combinations are valid</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {packageTypes.map((type) => {
                const selected = formData.package_types.includes(type.value);
                return (
                  <button
                    key={type.value}
                    onClick={() => togglePackageType(type.value)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      selected
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    data-testid={`package-type-${type.value.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <div className="text-2xl mb-1">{type.icon}</div>
                    <div className="font-semibold text-sm mb-0.5">{type.label}</div>
                    <div className="text-xs text-gray-600">{type.desc}</div>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-2 ${
                      selected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                    }`}>
                      {selected && <CheckCircle className="h-4 w-4 text-white" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900">Date Flexibility</h2>
              <p className="text-sm text-gray-600 mt-1">When can you travel?</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {datePreferences.map((pref) => (
                <button
                  key={pref.value}
                  onClick={() => setFormData({...formData, date_preference: pref.value})}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    formData.date_preference === pref.value
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  data-testid={`date-${pref.value}`}
                >
                  <div className="font-medium text-sm">{pref.label}</div>
                </button>
              ))}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900">{t('residencePrompt.title')}</h2>
              <p className="text-sm text-gray-600 mt-1">{t('residencePrompt.body')}</p>
            </div>
            <div className="max-w-md mx-auto space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1" data-testid="residence-country-label">{t('residencePrompt.label')} *</label>
                <select
                  value={residenceCountry}
                  onChange={(e) => setResidenceCountry(e.target.value)}
                  className="w-full p-3 rounded-lg border-2 border-gray-200 focus:border-blue-600 focus:outline-none text-sm"
                  data-testid="residence-country-select"
                >
                  <option value="">{t('residencePrompt.placeholder')}</option>
                  {countries.map((country) => (
                    <option key={`res-${country.code || country}`} value={country.code || country.name || country}>{country.name || country}</option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-500 mt-1">{t('residencePrompt.hint')}</p>
              </div>
              <label className="block text-xs font-medium text-gray-700 -mb-3" data-testid="origin-country-label">{t('residencePrompt.originLabel')} <span className="text-gray-400">— {t('residencePrompt.originHint')}</span></label>
              <select
                value={formData.country_of_origin}
                onChange={(e) => setFormData({...formData, country_of_origin: e.target.value})}
                className="w-full p-3 rounded-lg border-2 border-gray-200 focus:border-blue-600 focus:outline-none text-sm"
                data-testid="country-select"
              >
                <option value="">Select your country</option>
                {countries.map((country) => (
                  <option key={country.code || country} value={country.name || country}>{country.name || country}</option>
                ))}
              </select>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const progress = (step / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Toaster />
      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            TreatX
          </h1>
          <p className="text-sm text-gray-500 mt-1">Let's find your perfect treatment</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Step {step} of {totalSteps}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-1.5" />
        </div>

        {/* Content Card */}
        <Card className="p-5 shadow-lg">
          {renderStep()}

          {/* Navigation */}
          <div className="flex justify-between mt-5 pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={step === 1}
              data-testid="onboarding-back-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>

            {step < totalSteps ? (
              <Button onClick={handleNext} disabled={!canProceed()} data-testid="onboarding-next-btn">
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={!canProceed() || loading} data-testid="onboarding-complete-btn">
                {loading ? 'Saving...' : 'Complete'}
                <CheckCircle className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PatientOnboardingV2;
